<script setup>
import { computed } from 'vue'
import VueApexCharts from 'vue3-apexcharts'
import { formatMoney, seriesHasRevenue } from '@/Utils/siteTransactions'

// Revenue over time from a castr transactions `Summary.series` (§6e): one
// bucket per UTC day (or ISO week past 120 days), zero days filled. Used on
// the Overview (last 30 days) and on the Transactions tab (current range).
// Bars (and the "$ · N purchases" header) only render when the series carries
// money — a range with free grants only shows the empty slot instead of a
// chart of $0 ticks.
const props = defineProps({
	/** `summary.series` — `[{ date, gross, refunded, net, count }]` */
	series: {
		type: Array,
		default: () => [],
	},
	/** Paid purchase count for the header; defaults to the series `count` sum (which includes free grants). */
	purchases: {
		type: Number,
		default: null,
	},
	/** `summary.bucket` — `day` | `week` */
	bucket: {
		type: String,
		default: 'day',
	},
	currency: {
		type: String,
		default: 'USD',
	},
	height: {
		type: Number,
		default: 180,
	},
	loading: {
		type: Boolean,
		default: false,
	},
})

const points = computed(() => (Array.isArray(props.series) ? props.series : []).filter((p) => p && p.date))
const hasData = computed(() => points.value.length >= 1 && seriesHasRevenue(points.value))
const mixed = computed(() => props.currency === 'mixed')
const money = (n) => (mixed.value ? Number(n || 0).toLocaleString() : formatMoney(n, props.currency))

const dayMs = 86_400_000
const toUtcMs = (date) => {
	const t = Date.parse(`${String(date).slice(0, 10)}T00:00:00Z`)
	return Number.isFinite(t) ? t : null
}
const chartSeries = computed(() => [
	{ name: 'Net', data: points.value.map((p) => [toUtcMs(p.date), Number(p.net) || 0]) },
	{ name: 'Refunded', data: points.value.map((p) => [toUtcMs(p.date), Number(p.refunded) || 0]) },
])
const total = computed(() => points.value.reduce((s, p) => s + (Number(p.net) || 0), 0))
const purchases = computed(() => (Number.isFinite(props.purchases) ? props.purchases : points.value.reduce((s, p) => s + (Number(p.count) || 0), 0)))

const fmtDate = (ms) => {
	const d = new Date(ms)
	const opts = { month: 'short', day: 'numeric', timeZone: 'UTC' }
	if (props.bucket === 'week') {
		const end = new Date(ms + 6 * dayMs)
		return `${d.toLocaleDateString(undefined, opts)} – ${end.toLocaleDateString(undefined, opts)}`
	}
	return d.toLocaleDateString(undefined, { ...opts, year: points.value.length > 366 ? 'numeric' : undefined })
}

const options = computed(() => ({
	chart: {
		type: 'bar',
		stacked: false,
		toolbar: { show: false },
		zoom: { enabled: false },
		animations: { enabled: false },
		parentHeightOffset: 0,
		redrawOnParentResize: true,
		fontFamily: 'inherit',
	},
	colors: ['#2076FF', 'rgba(135, 148, 181, 0.55)'],
	plotOptions: { bar: { columnWidth: points.value.length > 45 ? '85%' : '60%', borderRadius: 2 } },
	dataLabels: { enabled: false },
	legend: { show: false },
	grid: {
		borderColor: 'rgba(255, 255, 255, 0.12)',
		strokeDashArray: 0,
		xaxis: { lines: { show: false } },
		yaxis: { lines: { show: true } },
		padding: { left: 4, right: 8, top: 0, bottom: 0 },
	},
	xaxis: {
		type: 'datetime',
		tooltip: { enabled: false },
		axisBorder: { show: false },
		axisTicks: { show: false },
		tickAmount: Math.min(6, Math.max(2, points.value.length - 1)),
		labels: { datetimeUTC: true, style: { colors: '#8794B5', fontSize: '11px', fontFamily: 'inherit' } },
	},
	yaxis: {
		min: 0,
		forceNiceScale: true,
		tickAmount: 3,
		labels: { style: { colors: '#8794B5', fontSize: '11px', fontFamily: 'inherit' }, formatter: (v) => money(v), offsetX: -4 },
	},
	tooltip: {
		shared: true,
		intersect: false,
		theme: 'dark',
		custom: ({ dataPointIndex }) => {
			const p = points.value[dataPointIndex]
			if (!p) return ''
			return `<div class="site-revenue-tip"><span class="site-revenue-tip__date">${fmtDate(toUtcMs(p.date))}</span>` +
				`<span>Net <strong>${money(p.net)}</strong></span>` +
				(Number(p.refunded) > 0 ? `<span>Refunded <strong>${money(p.refunded)}</strong></span>` : '') +
				`<span>${p.count || 0} ${Number(p.count) === 1 ? 'purchase' : 'purchases'}</span></div>`
		},
	},
	states: { active: { filter: { type: 'none' } }, hover: { filter: { type: 'lighten', value: 0.08 } } },
}))
</script>

<template>
	<div class="site-revenue-chart" data-testid="revenue-chart" :aria-busy="loading">
		<div v-if="hasData || loading" class="mb-1 flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
			<span class="text-base font-semibold text-white" data-testid="revenue-chart-total">
				<span v-if="loading && !hasData" class="inline-block h-4 w-20 animate-pulse rounded bg-surface-4 align-middle" />
				<template v-else>{{ money(total) }}</template>
			</span>
			<span v-if="hasData" class="text-tiny text-surface-7">net · {{ purchases }} {{ purchases === 1 ? 'purchase' : 'purchases' }}</span>
			<slot name="meta" />
		</div>
		<div v-if="hasData" :style="{ height: `${height}px` }" class="-mx-1">
			<VueApexCharts type="bar" height="100%" :options="options" :series="chartSeries" />
		</div>
		<div v-else class="grid place-items-center rounded-lg bg-surface-3 text-tiny text-surface-7" :style="{ height: `${Math.max(64, height * 0.6)}px` }" data-testid="revenue-chart-empty">
			<span v-if="loading" class="inline-block h-3 w-32 animate-pulse rounded bg-surface-4" />
			<slot v-else name="empty">No revenue in this period yet.</slot>
		</div>
	</div>
</template>

<style>
.site-revenue-chart .apexcharts-tooltip {
	background: rgb(20, 30, 61) !important;
	border: 1px solid rgba(255, 255, 255, 0.08) !important;
	border-radius: 8px !important;
	color: rgb(209, 219, 240);
	opacity: 0 !important;
	transition: opacity 0.12s ease;
}
.site-revenue-chart .apexcharts-tooltip.apexcharts-active {
	opacity: 1 !important;
}
.site-revenue-tip {
	display: flex;
	flex-direction: column;
	gap: 2px;
	padding: 8px 10px;
	font-size: 12px;
	line-height: 16px;
	min-width: 150px;
}
.site-revenue-tip__date {
	color: rgb(174, 186, 214);
	margin-bottom: 2px;
	font-weight: 500;
}
</style>
